import { z } from "zod";
import { runIdSchema, taskIdSchema } from "./primitives.js";
export const timeframeSchema = z.enum(["1h", "24h", "7d", "30d"]);
export const performanceDashboardQuerySchema = z.object({
    timeframe: timeframeSchema.default("24h"),
    now: z.coerce.number().finite().nonnegative().optional(),
}).strict();
export const runPerformanceMetricsSchema = z.object({
    queueWaitMs: z.number().optional(),
    workerQueueWaitMs: z.number().optional(),
    preparationMs: z.number().optional(),
    catalogLoadMs: z.number().optional(),
    routingLlmMs: z.number().optional(),
    routingLlmCalls: z.number().int().nonnegative().optional(),
    contractPersistMs: z.number().optional(),
    workerStartupMs: z.number().optional(),
    timeToFirstTokenMs: z.number().optional(),
    timeToFirstVisibleTextMs: z.number().optional(),
    modelWorkMs: z.number().optional(),
    totalRuntimeMs: z.number().optional(),
    endToEndMs: z.number().optional(),
    toolCallCount: z.number().optional(),
    toolErrorCount: z.number().optional(),
    sqlQueryCount: z.number().optional(),
    sqlTotalMs: z.number().optional(),
    sqlMaxMs: z.number().optional(),
    sqlRowCount: z.number().optional(),
});
export const performanceStatusSummarySchema = z.object({
    queued: z.number(),
    running: z.number(),
    completed: z.number(),
    failed: z.number(),
    cancelled: z.number(),
});
export const metricBenchmarkSchema = z.object({
    value: z.number().optional(),
    avg: z.number().optional(),
    p50: z.number().optional(),
    p95: z.number().optional(),
    percentile: z.number().optional(),
    sampleSize: z.number(),
});
export const metricDistributionSchema = z.object({
    p50: z.number().optional(),
    p95: z.number().optional(),
    max: z.number().optional(),
    sample: z.number().int().nonnegative(),
    eligible: z.number().int().nonnegative(),
    coverage: z.number().min(0).max(1),
    unavailableReason: z.string().optional(),
});
export const reliablePerformanceTimelinePointSchema = z.object({
    bucketStart: z.number(),
    completed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    visibleResponse: metricDistributionSchema,
    activeRuntime: metricDistributionSchema,
});
export const reliablePerformanceOutlierSchema = z.object({
    runId: runIdSchema,
    taskId: taskIdSchema,
    sessionId: z.string().min(1).optional(),
    taskTitle: z.string(),
    status: z.enum(["completed", "failed", "cancelled"]),
    finishedAt: z.number(),
    endToEndMs: z.number().optional(),
    visibleResponseMs: z.number().optional(),
    actualModel: z.string().optional(),
});
export const reliablePerformanceSchema = z.object({
    scope: z.object({
        generatedAt: z.number(),
        from: z.number(),
        to: z.number(),
        timeframe: timeframeSchema,
        organisationId: z.string().optional(),
    }),
    summary: z.object({
        terminal: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        cancelled: z.number().int().nonnegative(),
        successRate: z.number().min(0).max(1).optional(),
        retriedRuns: z.number().int().nonnegative(),
        modelCalls: z.number().int().nonnegative(),
        totalTokens: z.number().int().nonnegative().optional(),
        taskAgentCostMicrousd: z.number().int().nonnegative().optional(),
        internalCostMicrousd: z.number().int().nonnegative().optional(),
        allInCostMicrousd: z.number().int().nonnegative().optional(),
        costCapturedCalls: z.number().int().nonnegative(),
        cacheEvidenceCalls: z.number().int().nonnegative(),
        promptTokens: z.number().int().nonnegative().optional(),
        cacheReadTokens: z.number().int().nonnegative().optional(),
        cacheReadShare: z.number().min(0).max(1).optional(),
    }),
    latency: z.object({
        visibleResponse: metricDistributionSchema,
        queueWait: metricDistributionSchema,
        preparation: metricDistributionSchema,
        workerQueueWait: metricDistributionSchema,
        workerStartup: metricDistributionSchema,
        activeRuntime: metricDistributionSchema,
        endToEnd: metricDistributionSchema,
    }),
    workers: z.array(z.object({
        workerId: z.string(),
        attempts: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        leaseLost: z.number().int().nonnegative(),
        p50RuntimeMs: z.number().optional(),
        p95RuntimeMs: z.number().optional(),
    })),
    models: z.array(z.object({
        role: z.string(),
        requestedModel: z.string(),
        model: z.string(),
        calls: z.number().int().nonnegative(),
        tokens: z.number().int().nonnegative().optional(),
        providerCostMicrousd: z.number().int().nonnegative().optional(),
        estimatedCostMicrousd: z.number().int().nonnegative().optional(),
        usageCaptured: z.number().int().nonnegative(),
        costCaptured: z.number().int().nonnegative(),
        cacheEvidenceCalls: z.number().int().nonnegative(),
        promptTokens: z.number().int().nonnegative().optional(),
        cacheReadTokens: z.number().int().nonnegative().optional(),
        cacheReadShare: z.number().min(0).max(1).optional(),
    })),
    sources: z.array(z.object({
        source: z.string(),
        interactionMode: z.string(),
        terminal: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        cancelled: z.number().int().nonnegative(),
        retriedRuns: z.number().int().nonnegative(),
    })),
    timeline: z.array(reliablePerformanceTimelinePointSchema),
    outliers: z.array(reliablePerformanceOutlierSchema),
    errors: z.array(z.object({ code: z.string(), count: z.number().int().nonnegative() })),
});
export const runBenchmarkSnapshotSchema = z.object({
    timeframe: timeframeSchema,
    sampleSize: z.number(),
    statusCounts: performanceStatusSummarySchema,
    runId: runIdSchema,
    taskId: taskIdSchema,
    metricBenchmarks: z.object({
        queueWaitMs: metricBenchmarkSchema,
        workerQueueWaitMs: metricBenchmarkSchema,
        preparationMs: metricBenchmarkSchema,
        catalogLoadMs: metricBenchmarkSchema,
        routingLlmMs: metricBenchmarkSchema,
        contractPersistMs: metricBenchmarkSchema,
        timeToFirstTokenMs: metricBenchmarkSchema,
        timeToFirstVisibleTextMs: metricBenchmarkSchema,
        totalRuntimeMs: metricBenchmarkSchema,
        endToEndMs: metricBenchmarkSchema,
        sqlTotalMs: metricBenchmarkSchema,
    }),
    retrievalChildren: z.object({
        count: z.number().int().nonnegative(),
        runtimeMs: metricBenchmarkSchema,
        sqlQueryCount: z.number().int().nonnegative(),
    }),
    modelUsage: z.array(z.object({
        runtimeRole: z.string(),
        calls: z.number().int().nonnegative(),
        cacheReadTokens: z.number().int().nonnegative(),
    })),
    selfHostedConcurrency: z.object({
        available: z.boolean(),
        maxConcurrentClients: z.number().int().nonnegative(),
        calls: z.array(z.object({
            clientIndex: z.number().int().nonnegative(),
            currentRun: z.boolean(),
            startedAt: z.number().nonnegative(),
            durationMs: z.number().nonnegative(),
            decodeTokensPerSecond: z.number().nonnegative().optional(),
        })),
    }).optional(),
});
export const performanceDashboardSchema = reliablePerformanceSchema;
